import {
  Account,
  ChangeLog,
  Response,
  apiAbortController,
  AccountingJobProps as AccountingJob,
} from '@ui/src';
import { Category } from '@ui/src/components/accounts-selector/types';
import api from '../../../api/api';
import { AccountingJobValues, UpdateAccountingJobValues } from '../types';

export const accountingManagmentApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAccountingJobs: build.query<Response<AccountingJob>, string>({
      query: (query) => ({
        url: `/v1/accounting-jobs${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountingJobsStates: build.query<Response<AccountingJob>, string>({
      query: (query) => ({
        url: `/v1/accounting-jobs${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountingJobItem: build.query<{ data: AccountingJob }, string>({
      query: (id) => ({
        url: `/v1/accounting-jobs/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    retryAccountingJobItem: build.query<{ data: AccountingJob }, string>({
      query: (id) => ({
        url: `/v1/accounting-jobs/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createAccountingJob: build.mutation<AccountingJob, AccountingJobValues>({
      query: (body) => ({
        url: '/v1/accounting-jobs',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateAccountingJob: build.mutation<
      { data: AccountingJob },
      { id: string; body: UpdateAccountingJobValues }
    >({
      query: ({ id, body }) => ({
        url: `/v1/accounting-jobs/${id}`,
        method: 'PUT',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    startAccountingJob: build.mutation<{ data: AccountingJob }, string>({
      query: (id) => ({
        url: `/v1/accounting-jobs/${id}/start`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    cancelAccountingJob: build.mutation<{ data: AccountingJob }, string>({
      query: (id) => ({
        url: `/v1/accounting-jobs/${id}/cancel`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    restatementAccountingJob: build.mutation<
      { data: AccountingJob },
      AccountingJobValues
    >({
      query: (body) => ({
        url: '/v1/accounting-jobs/restatement',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountingJobsChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountingManagmentAccountsCategories: build.query<
      Response<Category>,
      string
    >({
      query: (query) => ({
        url: `/v1/accounts-categories?${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountingManagmentAccountsList: build.query<Response<Account>, string>({
      query: (query) => ({
        url: `/v1/accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountingManagmentJobAccounts: build.query<Response<Account>, string>({
      query: (query) => ({
        url: `/v1/accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetAccountingJobItemQuery,
  useLazyGetAccountingJobsQuery,
  useCreateAccountingJobMutation,
  useCancelAccountingJobMutation,
  useRestatementAccountingJobMutation,
  useStartAccountingJobMutation,
  useUpdateAccountingJobMutation,
  useLazyGetAccountingJobsChangeLogsQuery,
  useLazyGetAccountingJobsStatesQuery,
  useLazyGetAccountingManagmentAccountsCategoriesQuery,
  useGetAccountingManagmentAccountsCategoriesQuery,
  useLazyGetAccountingManagmentAccountsListQuery,
  useLazyGetAccountingManagmentJobAccountsQuery,
  useLazyRetryAccountingJobItemQuery,
} = accountingManagmentApi;

export const {
  endpoints: {
    getAccountingJobItem,
    getAccountingJobs,
    createAccountingJob,
    cancelAccountingJob,
    startAccountingJob,
    updateAccountingJob,
    restatementAccountingJob,
    getAccountingJobsChangeLogs,
    getAccountingJobsStates,
    getAccountingManagmentAccountsCategories,
    getAccountingManagmentAccountsList,
    getAccountingManagmentJobAccounts,
    retryAccountingJobItem,
  },
} = accountingManagmentApi;
